<template>
  <div class="contain">
    <!-- 검색 영역 -->
    <div class="search_form" ref="search_form">
      <el-form
        :inline="true"
        :label-position="$device.mobile ? `top` : `left`"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row>
          <el-col :md="12">
            <el-form-item :label="$t('대상 매장')">
              <span class="dm-scope">{{ scopeStoreName(storeCd) }}</span>
              <span class="dm-weak">&nbsp;{{ $t('— 이 매장이 속한 샵인샵 그룹 전체를 봅니다') }}</span>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <!-- 버튼 -->
    <div class="buttons" ref="buttons">
      <el-button :size="size" @click="search" icon="el-icon-search">{{ $t('검색') }}</el-button>
    </div>

    <!-- 리스트 -->
    <el-table
      :height="tableHeight"
      ref="table"
      border
      stripe
      tooltip-effect="dark"
      :data="matrix.menus"
      :size="size"
      :empty-text="emptyText">
      <el-table-column :label="$t('메뉴')" fixed min-width="180" show-overflow-tooltip>
        <template slot-scope="s">
          {{ s.row.smenuNm }}
          <div class="dm-weak">#{{ s.row.originalSmenuCd }}</div>
        </template>
      </el-table-column>

      <el-table-column v-for="store in matrix.stores" :key="store.storeCd" :label="storeHeader(store)" align="center">
        <el-table-column
          v-for="platform in matrix.platforms"
          :key="store.storeCd + '-' + platform"
          :label="platformName(platform)"
          min-width="150"
          align="center">
          <template slot-scope="s">
            <template v-if="cellsAt(s.row.originalSmenuCd, store.storeCd, platform).length">
              <delivery-menu-chip
                v-for="cell in cellsAt(s.row.originalSmenuCd, store.storeCd, platform)"
                :key="cell.status.linkId || cell.smenuCd + '-' + platform"
                :status="cell.status"
                @select="openCell(cell)" />
            </template>
            <span v-else class="dm-weak">-</span>
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>

    <delivery-menu-detail-dialog
      :visible.sync="detailVisible"
      :smenu-cd="detailSmenuCd"
      :store-cd="detailStoreCd"
      @changed="search"
      @open-menu="openMenu"
      @go-orphan="goOrphan"
      @go-drift="goDrift" />
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import deliveryMenuScopeMixin from '@/mixins/deliveryMenuScopeMixin'
import { initData } from '@/utils/constants'
import DeliveryMenuChip from '@/components/deliveryMenu/DeliveryMenuChip.vue'
import DeliveryMenuDetailDialog from '@/components/deliveryMenu/DeliveryMenuDetailDialog.vue'
import { DELIVERY_MENU_API, deliveryMenuErrorMessage, deliveryMenuPlatformName } from '@/utils/deliveryMenuSync'

/**
 * ② 그룹 통합 매트릭스 — `GET /matrix/{storeCd}`.
 *
 * **축 조립은 프론트 몫이다** — 서버는 `stores`·`platforms`·`menus`·`cells` 네 배열을 담아 내리고
 * 교차표를 만들지 않는다. `(originalSmenuCd, storeCd, platform)` 인덱스를 여기서 만든다.
 *
 * 한 칸에 링크가 여럿일 수 있어(같은 샵에 판이 둘, 또는 한 매장이 같은 플랫폼에 샵을 여럿)
 * `cells` 는 링크마다 한 항목이다 — 접지 않고 전부 쌓는다.
 *
 * `use_yn='N'` 행도 셀에 온다 (§16.53). 그 링크는 `STALE` 이며 chip 이 「오더엔에서 내린 메뉴 · <마지막
 * 작업 상태>」로 구분해 보여준다(§16.302 — 숨김 push 의 결과가 뒤에 붙고, 작업이 없으면 접두만) — 빈 칸으로
 * 그리면 "연동 안 됨" 과 섞인다.
 */
export default {
  name: 'DeliveryMenuMatrixView',
  mixins: [mainVueMixin, deliveryMenuScopeMixin],
  components: { DeliveryMenuChip, DeliveryMenuDetailDialog },

  data() {
    const data = initData()
    data.matrix = { storeCd: null, stores: [], platforms: [], menus: [], cells: [] }
    data.detailVisible = false
    data.detailSmenuCd = null
    data.detailStoreCd = null
    return data
  },

  computed: {
    storeCd() {
      return this.scopeStoreCd
    },
    /** `(원본, 매장, 플랫폼)` → 셀 목록. 링크마다 한 항목이라 값이 배열이다 */
    cellIndex() {
      const index = {}
      for (const cell of this.matrix.cells || []) {
        const key = `${cell.originalSmenuCd}|${cell.storeCd}|${cell.status.platform}`
        if (!index[key]) index[key] = []
        index[key].push(cell)
      }
      return index
    },
    emptyText() {
      if (!this.matrix.stores || this.matrix.stores.length === 0) return this.$t('데이타 없음')
      return this.$t('아직 연동된 메뉴가 없습니다.')
    },
  },

  async created() {
    await this.loadScopeStore()
    this.search()
  },

  mounted() {
    this.$nextTick(() => {
      this.mainVue.currVue = this
    })
  },

  methods: {
    platformName: deliveryMenuPlatformName,

    storeHeader(store) {
      const name = store.storeNm || this.scopeStoreName(store.storeCd)
      return store.primary ? `${name} (${this.$t('대표')})` : name
    },

    cellsAt(originalSmenuCd, storeCd, platform) {
      return this.cellIndex[`${originalSmenuCd}|${storeCd}|${platform}`] || []
    },

    async search() {
      if (this.storeCd == null) return
      try {
        const res = await this.$axios.get(`${DELIVERY_MENU_API}/matrix/${this.storeCd}`)
        this.matrix = res.data || { stores: [], platforms: [], menus: [], cells: [] }
        // 매장명의 출처는 이 응답이다 — 형제 매장 이름을 따로 조회하지 않는다(mixin 주석 참조)
        this.rememberStoreNames(this.matrix.stores)
      } catch (e) {
        this.matrix = { stores: [], platforms: [], menus: [], cells: [] }
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
      }
    },

    openCell(cell) {
      this.detailSmenuCd = cell.smenuCd
      this.detailStoreCd = cell.storeCd
      this.detailVisible = true
    },

    openMenu(smenuCd) {
      this.detailSmenuCd = smenuCd
      this.detailVisible = true
    },

    goOrphan() {
      this.$router.push('/v/business/delivery-menu/orphans')
    },

    goDrift() {
      this.$router.push('/v/business/delivery-menu/drifts')
    },
  },
}
</script>

<style scoped>
.dm-scope {
  font-weight: 600;
}
.dm-weak {
  color: #909399;
  font-size: 12px;
}
</style>
